/**
 * Pure click-classification predicates for drag-to-select surfaces.
 *
 * They decide whether a mousedown should be treated as a "plain click" that clears the current
 * selection, or ignored (click on a menu, a button, a scrollbar, a selection-disabled region…).
 * They live here — outside the useSelectedItemsMouseSelection shell — so OTHER selection
 * surfaces (e.g. the ItemPicker marquee) can adopt the same rules without pulling in the
 * central selected-items Redux/analytics policy.
 */

const isButton = (target: Element) => target.tagName === 'BUTTON' || target.getAttribute('role') === 'button';

/** `dataset` lives on HTML and SVG elements. Other `Element`s (MathML) have nothing that opts out of selection. */
const selectionDataset = (element: Element) =>
  element instanceof HTMLElement || element instanceof SVGElement ? element.dataset : undefined;

/**
 * True when the click landed inside a region that opted out of drag-to-select: form inputs, or any
 * ancestor marked `data-draggable` / `data-disableselect`. Used both to skip deselect-on-click and
 * to veto starting a marquee drag.
 */
export const isSelectionDisabled = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false;
  }

  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
    return true;
  }

  let el: Element = target;

  while (
    el.parentElement &&
    el.tagName !== 'BODY' &&
    el.tagName !== 'MAIN' &&
    !selectionDataset(el)?.draggable &&
    !selectionDataset(el)?.disableselect
  ) {
    el = el.parentElement;
  }

  return selectionDataset(el)?.draggable === 'true' || selectionDataset(el)?.disableselect === 'true';
};

// This is a bit hacky way to determine if we should clear selectable items
// Do not clear them if a user clicked an action in actions container (it will be removed soon)
export const isMenuOrButtonClicked = (target: Element) => {
  let el: Element = target;

  while (el.parentElement && !isButton(el) && el.tagName !== 'REACH-PORTAL' && el.tagName !== 'MAIN') {
    el = el.parentElement;
  }

  // actions container has data-select-actions-container=true
  return isButton(el);
};

/**
 * True while a Radix dropdown or context menu (including their submenus) is open anywhere in the
 * document — every menu in the app renders the shared `@radix-ui/react-menu` content — or while an
 * overlay that opted in with `data-preserves-selection` is in the document (the watermark image picker).
 *
 * MUST be called AT POINTERDOWN TIME, in the capture phase: Radix dismisses on a document-level
 * pointerdown-outside listener and unmounts the overlay synchronously inside it, so by the time a
 * mousedown handler runs the overlay is already out of the DOM and this always returns false.
 */
export const isAnyMenuOverlayOpen = () =>
  !!document.querySelector('[data-radix-menu-content][data-state="open"], [data-preserves-selection]');

/**
 * True when the mousedown hit the scroll viewport itself.
 *
 * if user clicks and event.target is same as scrollElement, it's 99% chance that click is on
 * scrollbar, because it's the only area in scrollElement which is not filled with our components
 */
export const isScrollbarClick = (target: EventTarget | null, scrollElement: HTMLElement | null) =>
  target === scrollElement;
